import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";
import { useDispatch, useSelector } from "react-redux";

import { useAudioContext } from "../../contexts/AudioContext";
import {
  getBookIdWithPrefix, isSameBook
} from "../../utils/content/content";
import { useGetCurrentPlayingPara } from "../../contexts/AudioContext.hooks";
import { isNumberBetweenEquals } from "../../utils/number/number";
import { selectorPlayerWaveformsDictionary } from "../../store/player/selectors";
import { selectorSetting } from "../../store/settings/selectors";
import PlayerBehaviourDialog from "../../components/PlayerBehaviourDialog/PlayerBehaviourDialog";
import { _sharedActionsSettings } from "../../store/settings/actions";
import { settingsActions } from "../../../main/src/redux/settings/actions";
import { systemActions } from "../../../main/src/redux/system/system.actions";
import { TOAST_TYPE } from "../../constants";
import {
  READER_PLAYER_BEHAVIOURS
} from "../../utils/reader/constants";
import {
  findWaveformsDictionaryParaById,
  getFirstAvailableAudioChapter
} from "../../utils/player";
import { getReaderAudioChapter } from "../../utils/reader/reader";
import { SETTING } from "../../utils/settings/constants";
import { useModalContext } from "../../contexts/modal";
import { selectorSystemIsLogin } from "@/shared/store/system/selectors";

export const READER_PLAYER_DIALOG_ID = "rpl/d";

/**
 * @typedef ReaderPlayerHook

 * @property indexPanelInPlayer: number,

 * @property isInPlayerAnotherBook: boolean | undefined,
 * @property isInPlayerAnotherPara: boolean | undefined,
 * @property isInPlayerAnotherChapter: boolean | undefined,
 * @property isCurrentParaHasAudio: (boolean|undefined),

 * @property waveformsDictionary: {},
 * @property activeChapterAudioChapter: {} | null,
 * @property firstAvailableAudioChapter: {} | null,
 * @property currentPlayingPara: {} | null

 * @property syncPlayerAndReader: boolean,
 * @property enableSyncReaderAndPlayer: () => void,
 * @property stopSyncPlayerAndReader: () => void,
 * @property handlePlayClick: () => void,

 * @property handleOpenPanel: () => void,
 * @property handleClosePanel: () => void,
 * @property handleSwapPanels: () => void,
 */

/**
 * @return {ReaderPlayerHook}
 */
const useReaderPlayer = ({
    book,
    toc,
    index,
    activeChapterId,
    activeId,
    fromML,
    isIgnoreSettings,
    bookHistory,
    isBookAudioHistoryFetching,
    updateActivePanel,
    onShowPlayTypeDialog,
  }
) => {
  const bookId = getBookIdWithPrefix(activeChapterId);
  const bookFirstPara = book ? book.first_para : undefined;

  const refIsPopupShownAfterLiveReadingDesync = useRef(false);

  const { t } = useSharedTranslation();
  const dispatch = useDispatch();

  const [indexPanelInPlayer, setIndexPanelInPlayer] = useState(index);

  const {
    toggleChapterPlaying: _toggleChapterPlaying,
    playChapterAndShowPlayer: _playChapterAndShowPlayer,
    isPlaying,
    chapterId,
    audioChapterId,
    getPlayerCurrentTime,
    downloadOrPullFromSWWaveformsData,
  } = useAudioContext();
  const {
    showModal,
    hideDialog,
    checkDialogId
  } = useModalContext();

  const toggleChapterPlaying = (...args) => {
    _toggleChapterPlaying(...args);

    if (indexPanelInPlayer !== index) {
      setIndexPanelInPlayer(index);
    }
  };
  const playChapterAndShowPlayer = (...args) => {
    _playChapterAndShowPlayer(...args);

    if (indexPanelInPlayer !== index) {
      setIndexPanelInPlayer(index);
    }
  };

  const currentPlayingPara = useGetCurrentPlayingPara();

  const waveformsDictionary = useSelector(selectorPlayerWaveformsDictionary);
  const paraChangePlayerBehaviour = useSelector(
    selectorSetting(SETTING.paraChangePlayerBehaviour.id)
  );
  const isLogin = useSelector(selectorSystemIsLogin);
  const isIgnoreSettingsFinal = isIgnoreSettings ?? !isLogin;
  const bookChangePlayerBehaviour = useSelector(
    selectorSetting(SETTING.bookChangePlayerBehaviour.id)
  );
  const syncPlayerAndReader = useSelector(selectorSetting(SETTING.syncPlayerAndReader.id));

  const isReaderBehaviourDialogShown = checkDialogId(READER_PLAYER_DIALOG_ID);

  const firstAvailableAudioChapter = useMemo(() => {
    return getFirstAvailableAudioChapter(toc);
  }, [toc]);

  const activeChapterAudioChapter = useMemo(() => {
    if (activeChapterId === undefined) {
      return undefined;
    }

    if (bookFirstPara && activeChapterId === bookFirstPara) {
      return getFirstAvailableAudioChapter(toc);
    }

    return getReaderAudioChapter(toc, activeChapterId);
  }, [toc, activeChapterId, bookFirstPara]);

  const activeParaPositions = findWaveformsDictionaryParaById(
    waveformsDictionary, activeChapterAudioChapter?.id, activeId
  );

  const isActiveAudioChapterPositionsFetching = activeChapterAudioChapter === undefined;

  let isCurrentParaHasAudio;
  let isCurrentParaAudioPositionsLoading = false;

  if (book && book.is_audiobook && toc?.length) {
    isCurrentParaAudioPositionsLoading = isActiveAudioChapterPositionsFetching || !activeParaPositions;
    isCurrentParaHasAudio = !!activeChapterAudioChapter;
  } else {
    isCurrentParaHasAudio = false;
  }

  const isInPlayerAnotherBook = (!chapterId || !activeId)
    ? false
    : !isSameBook(chapterId, activeId);

  const isInPlayerAnotherChapter = (isActiveAudioChapterPositionsFetching || !chapterId)
    ? false
    : activeChapterAudioChapter?.id !== audioChapterId;

  const isInPlayerAnotherPara = (!currentPlayingPara || !activeId || !chapterId)
    ? false
    : activeId !== currentPlayingPara.id;

  /* TODO: Can be called on the play click, but fetching loader should be implemented then. */
  // Fetches current Reader chapter audio positions.
  useEffect(() => {
    downloadOrPullFromSWWaveformsData(activeChapterAudioChapter);
  }, [activeChapterAudioChapter]);


  const getLibraryBookHistory = () => {
    if (bookHistory?.listenChapterId) {
      return {
        chapter: bookHistory.listenChapterId,
        position: bookHistory.listenPosition
      };
    }

    return null;
  };

  const getBookLastPlayed = () => {
    if (isInPlayerAnotherBook === false && chapterId) {
      return {
        chapter: chapterId,
        audioChapterId,
        position: getPlayerCurrentTime(),
      };
    }

    return null;
  };

  const showToastParaAutoReadingIsNotAvailable = () => {
    dispatch(systemActions.toast(t("paraAutoReadNotAvailable"), TOAST_TYPE.warning));
  };

  const enableSyncReaderAndPlayer = () => {
    dispatch(_sharedActionsSettings.updateSettings(SETTING.syncPlayerAndReader.id, true));
  };

  const stopSyncPlayerAndReader = () => {
    if (syncPlayerAndReader && isPlaying) {
      dispatch(settingsActions.updateSettings(SETTING.syncPlayerAndReader.id, false));
      dispatch(systemActions.toast("@audioSyncDisabled", TOAST_TYPE.warning));
    }
  };


  const playAudioFromCurrentPara = async () => {
    if (activeChapterAudioChapter) {
      const para = activeParaPositions ?? activeChapterAudioChapter

      await updateActivePanel(para ? para.id : activeChapterAudioChapter.id);

      playChapterAndShowPlayer(activeChapterAudioChapter.id, para ? para.start : 0);
      enableSyncReaderAndPlayer();

      return true;
    } else {
      showToastParaAutoReadingIsNotAvailable();

      return false;
    }
  };

  const playAudioFromHistory = async () => {
    const history = getLibraryBookHistory();

    if (history) {
      await playAudioFromCurrentPara(history.chapter);
      playChapterAndShowPlayer(history.chapter, history.position, {
        fromML,
      });
      enableSyncReaderAndPlayer();

      return true;
    }

    return false;
  };

  const playAudioFirstAvailable = async () => {
    if (firstAvailableAudioChapter) {
      await playAudioFromCurrentPara(firstAvailableAudioChapter.id);
      playChapterAndShowPlayer(firstAvailableAudioChapter.id, 0, {
        fromML,
      });
      enableSyncReaderAndPlayer();

      return true;
    }

    return false;
  };

  const playAudioContinue = async () => {
    if (!chapterId) {
      return playAudioFromCurrentPara();
    }

    if (!isPlaying) {
      await updateActivePanel(currentPlayingPara?.id || chapterId);
      toggleChapterPlaying(chapterId);

      return true;
    }

    return false;
  };


  const handleReaderPlayerBehaviour = (behaviourType) => {
    if (behaviourType === READER_PLAYER_BEHAVIOURS.CONTINUE) {
      return playAudioContinue();
    } else if (behaviourType === READER_PLAYER_BEHAVIOURS.HISTORY) {
      return playAudioFromHistory();
    } else if (behaviourType === READER_PLAYER_BEHAVIOURS.CURRENT_PARA) {
      return playAudioFromCurrentPara();
    } else if (behaviourType === READER_PLAYER_BEHAVIOURS.FIRST_AVAILABLE) {
      return playAudioFirstAvailable();
    }
    return false;
  };


  const showPlayTypeDialog = () => {
    if (onShowPlayTypeDialog) {
      onShowPlayTypeDialog();
    }

    const isShowBtnPlayCurrChapter = !!activeChapterAudioChapter?.mp3;
    const isShowBtnPlayFirstAvailable = isShowBtnPlayCurrChapter
      ? false
      : !!firstAvailableAudioChapter;
    const isShowBtnPlayFromHistory = isInPlayerAnotherBook
      ? !!getLibraryBookHistory()
      : !!getLibraryBookHistory() && !chapterId;

    let countShownButtons = isShowBtnPlayCurrChapter + isShowBtnPlayFromHistory
      + isShowBtnPlayFirstAvailable;

    const isShowBtnPlayContinue = !!chapterId && countShownButtons > 0;
    if (isShowBtnPlayContinue) {
      countShownButtons += 1;
    }

    const isShowCheckboxDoNotAskAgain = !isIgnoreSettingsFinal && countShownButtons > 1;

    const isDoNotAskChecked = isInPlayerAnotherBook
      ? !bookChangePlayerBehaviour === READER_PLAYER_BEHAVIOURS.ASK
      : !paraChangePlayerBehaviour === READER_PLAYER_BEHAVIOURS.ASK;

    let subtitle;

    const isCanPlayCurrentBook = isShowBtnPlayCurrChapter || isShowBtnPlayFirstAvailable;

    if (isCanPlayCurrentBook) {
      if (isInPlayerAnotherBook) {
        subtitle = t("smthChangedHowToProceed", {
          smth: t("book").toLowerCase(),
        });
      } else if (isInPlayerAnotherChapter || isInPlayerAnotherPara) {
        subtitle = t("smthChangedHowToProceed", {
          smth: t(isInPlayerAnotherChapter ? "chapter" : "paragraph").toLowerCase(),
        });
      }
    } else {
      subtitle = t("audioNotAvailable");
    }

    showModal(
      <PlayerBehaviourDialog
        subtitle={subtitle}
        canContinue={isShowBtnPlayContinue}
        canDoNotAskAgain={isShowCheckboxDoNotAskAgain}
        canPlayFromHistory={isShowBtnPlayFromHistory}
        canPlayFirstAvailable={isShowBtnPlayFirstAvailable}
        canPlayFromCurrentPara={isShowBtnPlayCurrChapter}
        isDoNotAskChecked={isDoNotAskChecked}
        onClickBehaviour={(type, doNotAskAgain) => {
          if (doNotAskAgain) {
            if (isInPlayerAnotherBook) {
              dispatch(_sharedActionsSettings.updateSettings(
                SETTING.bookChangePlayerBehaviour.id, type
              ));
            } else {
              dispatch(_sharedActionsSettings.updateSettings(
                SETTING.paraChangePlayerBehaviour.id, type
              ));
            }
          }

          handleReaderPlayerBehaviour(type);
          hideDialog();
        }}
      />, {
        dialogId: READER_PLAYER_DIALOG_ID,
      }
    );
  };


  const handlePlayClick = async () => {
    // If is playing.
    if (isPlaying) {
      toggleChapterPlaying(chapterId);
      return;
    }
    // ========

    // If isnt playing and Reader and Player books are different.
    if (isInPlayerAnotherBook) {
      if (
        bookChangePlayerBehaviour === READER_PLAYER_BEHAVIOURS.ASK
        || !bookChangePlayerBehaviour || isIgnoreSettingsFinal
      ) {
        showPlayTypeDialog();
      } else {
        const success = await handleReaderPlayerBehaviour(bookChangePlayerBehaviour);
        if (!success) {
          showPlayTypeDialog();
        }
      }

      return;
    }
    // ========

    // If isnt playing and Player and Reader books are the same, and book has listen history.
    const bookAudioHistory = getBookLastPlayed() || getLibraryBookHistory();
    if (bookAudioHistory) {
      const {
        chapter,
        position
      } = bookAudioHistory;
      const activeChapterAudioChapterId = activeChapterAudioChapter?.id;

      const audioChapter = getReaderAudioChapter(toc, chapter);
      const audioChapterId = audioChapter ? audioChapter.id : chapter;

      if (
        audioChapterId === activeChapterAudioChapterId && (
          activeParaPositions
            ? isNumberBetweenEquals(position, activeParaPositions.start, activeParaPositions.end)
            : true
        )
      ) { // If Reader paragraph is the same as listen history paragraph.
        if (chapterId) {
          toggleChapterPlaying(chapter, position);
        } else {
          playChapterAndShowPlayer(audioChapterId, position, {
            fromML,
          });
          enableSyncReaderAndPlayer();
        }
      } else if (
        !paraChangePlayerBehaviour
        || paraChangePlayerBehaviour === READER_PLAYER_BEHAVIOURS.ASK
        || isIgnoreSettingsFinal
      ) { // Else if not authorized or setting is set to "ASK".
        showPlayTypeDialog();
      } else { // Else act with the saved behaviour.
        const success = await handleReaderPlayerBehaviour(paraChangePlayerBehaviour);
        if (!success) {
          showPlayTypeDialog();
        }
      }

      return;
    }
    // ========

    if (isCurrentParaHasAudio) {
      await playAudioFromCurrentPara();
    } else if (activeChapterAudioChapter?.mp3) {
      await playAudioFromCurrentPara();
    } else {
      showPlayTypeDialog();
    }
  };

  const handleOpenPanel = (indexNewPanel) => {
    if (indexNewPanel === indexPanelInPlayer || indexNewPanel < indexPanelInPlayer) {
      setIndexPanelInPlayer(indexPanelInPlayer + 1)
    }
  }

  const handleClosePanel = (indexNewPanel) => {
    if (indexNewPanel === indexPanelInPlayer) {
      setIndexPanelInPlayer(null)
    } else if (indexNewPanel < indexPanelInPlayer) {
      setIndexPanelInPlayer(indexPanelInPlayer - 1)
    }
  }

  const handleSwapPanels = (indexPanel1, indexPanel2) => {
    if (indexPanel1 === indexPanelInPlayer) {
      setIndexPanelInPlayer(indexPanel2)
    } else if (indexPanel2 === indexPanelInPlayer) {
      setIndexPanelInPlayer(indexPanel1)
    }
  }

  // Shows the "Book/chapter/para changed. How to proceed audio?" dialog.
  useEffect(() => {
    if (
      !isPlaying ||

      isCurrentParaAudioPositionsLoading ||
      isBookAudioHistoryFetching ||

      isReaderBehaviourDialogShown ||
      // prevent show popup more than once after sync is stopped.
      refIsPopupShownAfterLiveReadingDesync.current
    ) {
      if (book && activeId === book.book_id + ".0") {
        stopSyncPlayerAndReader();
      }
      
      return;
    }

    if (isInPlayerAnotherBook || isInPlayerAnotherChapter || isInPlayerAnotherPara) {
      const settingBehaviour = isInPlayerAnotherBook
        ? bookChangePlayerBehaviour
        : paraChangePlayerBehaviour;

      if (
        isIgnoreSettingsFinal
        || !settingBehaviour
        || settingBehaviour === READER_PLAYER_BEHAVIOURS.ASK
      ) {
        stopSyncPlayerAndReader();
        showPlayTypeDialog();
        refIsPopupShownAfterLiveReadingDesync.current = true;
      } else {
        handleReaderPlayerBehaviour(settingBehaviour);
      }
    }
  }, [
    isCurrentParaAudioPositionsLoading,
    isBookAudioHistoryFetching,
    isCurrentParaHasAudio,

    bookId,
    activeChapterId,
    activeId,
  ]);

  // Change Reader chapter on Player chapter change
  useEffect(() => {
    const idCurrentPlayingPara = currentPlayingPara?.id || chapterId;
    if (isPlaying && syncPlayerAndReader) {
      updateActivePanel(idCurrentPlayingPara);
    }
  }, [isPlaying, chapterId, currentPlayingPara]);

  const isLiveReadingActive = !!chapterId && syncPlayerAndReader;
  useEffect(() => {
    if (isLiveReadingActive) {
      refIsPopupShownAfterLiveReadingDesync.current = false;
    }
  }, [isLiveReadingActive]);

  return {
    indexPanelInPlayer,

    syncPlayerAndReader,

    isInPlayerAnotherBook,
    isInPlayerAnotherChapter,
    isInPlayerAnotherPara,

    waveformsDictionary,

    currentPlayingPara,
    activeChapterAudioChapter,
    firstAvailableAudioChapter,

    handlePlayClick,
    enableSyncReaderAndPlayer,
    stopSyncPlayerAndReader,

    handleOpenPanel,
    handleClosePanel,
    handleSwapPanels,
  };
};

export default useReaderPlayer;
